import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Text } from '@cuemath/web-comps';
import { COLORS, Separator } from '@cuemath/comps';
import styled from 'styled-components';

import VideoPlayer from './components/VideoPlayer';
import TranscriptPanel from './components/TranscriptPanel';
import { TABS } from './index';
import { getTeacherClassroom, getTeacherClassroomSummary } from '../../api/regular-class-analytics';
import { checkTranscriptStatus } from '../../api/trial-analytics';
import { useConfigPost } from '../../actions/configs';
import { useTranscribePost } from '../../actions/transcribe';
import {
  formatClassDate,
  parseBucketKey,
  extractVideoUrlFromLogUrl,
  buildS3Uri,
  mergeClassroomSummary,
} from '../../utils/classroom-helpers';
import { trackEvent } from 'modules/analytics';
import { EVENT_TYPE } from 'constants/logger';

const TRANSCRIPT_POLL_INTERVAL = 30000;
const TRANSCRIPT_MAX_ATTEMPTS = 20;
const COMPOSE_POLL_INTERVAL = 60000;
const COMPOSE_MAX_ATTEMPTS = 15;

const Container = styled.div`
  width: inherit;
  height: inherit;
  padding: 24px;
  display: flex;
  flex-direction: column;
`;

const BackButton = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: ${COLORS.COLA};
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 16px;
  width: fit-content;

  &:hover {
    text-decoration: underline;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const Badge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${COLORS.PALE_GREY};
  color: ${COLORS.DARK};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 450px;
  gap: 20px;
  flex: 1;
  min-height: 0;
`;

const VideoSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const TranscriptSection = styled.div`
  display: flex;
  flex-direction: column;
  background: ${COLORS.WHITE};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  max-height: calc(100vh - 200px);
`;

const ErrorWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
`;

const ComposingMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${COLORS.WHITE};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 48px 32px;
  text-align: center;
`;

function ClassroomViewPage() {
  const { teacherClassroomId } = useParams();
  const history = useHistory();
  const location = useLocation();

  // Classroom metadata
  const [classroom, setClassroom] = useState(location.state?.classroom || null);
  const [classroomLoading, setClassroomLoading] = useState(!classroom);
  const [classroomError, setClassroomError] = useState(null);

  // Derive rawVideoUrl reactively from classroom.log_url (not a one-shot initializer)
  const rawVideoUrl = useMemo(
    () => extractVideoUrlFromLogUrl(classroom?.log_url),
    [classroom],
  );

  // Video state
  const videoRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoError, setVideoError] = useState(null);
  const [videoNotComposed, setVideoNotComposed] = useState(false);
  const [composePollingMsg, setComposePollingMsg] = useState('');
  const composePollingRef = useRef(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);

  // Signed URL generation
  const { post: createAWSSignedUrl } = useConfigPost({
    onComplete: (errorMessage, signedUrl) => {
      if (errorMessage) {
        setVideoError('Failed to generate video URL.');
      } else {
        setVideoUrl(signedUrl);
      }
    },
  });

  // Polling signed URL generator
  const { post: pollSignedUrl } = useConfigPost({
    onComplete: (errorMessage, signedUrl) => {
      if (!errorMessage && signedUrl) {
        setVideoUrl(signedUrl);
        setVideoNotComposed(false);
        setComposePollingMsg('');
        if (composePollingRef.current) {
          clearInterval(composePollingRef.current);
          composePollingRef.current = null;
        }
      }
    },
  });

  // Video error — trigger compose and start polling
  const handleVideoError = useCallback(() => {
    if (videoNotComposed) return;

    const logUrl = classroom?.log_url;
    if (logUrl) {
      fetch(logUrl, { method: 'GET', credentials: 'include', mode: 'no-cors' }).catch(() => {});
      trackEvent(EVENT_TYPE.CLASSROOM_COMPOSE_TRIGGERED, {
        teacher_classroom_id: teacherClassroomId,
      });
    }
    setVideoNotComposed(true);
    setComposePollingMsg('Video composition triggered. Checking every 60 seconds...');

    if (rawVideoUrl) {
      const parsed = parseBucketKey(rawVideoUrl);
      if (parsed) {
        let attempts = 0;
        composePollingRef.current = setInterval(() => {
          attempts++;
          setComposePollingMsg(
            `Video composition in progress. Checking... (attempt ${attempts})`,
          );
          pollSignedUrl(parsed);
          if (attempts >= COMPOSE_MAX_ATTEMPTS) {
            clearInterval(composePollingRef.current);
            composePollingRef.current = null;
            setComposePollingMsg(
              'Composition is taking longer than expected. Please refresh the page later.',
            );
          }
        }, COMPOSE_POLL_INTERVAL);
      }
    }
  }, [classroom, teacherClassroomId, videoNotComposed, rawVideoUrl, pollSignedUrl]);

  // Cleanup compose polling on unmount
  useEffect(() => {
    return () => {
      if (composePollingRef.current) {
        clearInterval(composePollingRef.current);
      }
    };
  }, []);

  // Sign video URL when rawVideoUrl becomes available
  useEffect(() => {
    if (!rawVideoUrl) return;
    const parsed = parseBucketKey(rawVideoUrl);
    if (parsed) {
      createAWSSignedUrl(parsed);
    } else {
      setVideoError('Invalid video URL format.');
    }
  }, [rawVideoUrl, createAWSSignedUrl]);

  // Transcript state
  const [transcript, setTranscript] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(true);
  const [transcriptProcessing, setTranscriptProcessing] = useState(false);
  const [transcriptError, setTranscriptError] = useState(null);
  const pollingRef = useRef(null);

  // Transcription trigger
  const { post: startTranscribe } = useTranscribePost({
    onComplete: (error) => {
      if (error) {
        setTranscriptError('Failed to trigger transcription.');
        setTranscriptProcessing(false);
      }
    },
  });

  // Fetch classroom data if not passed via router state
  useEffect(() => {
    if (classroom) {
      setClassroomLoading(false);
      return;
    }

    const fetchClassroom = async () => {
      try {
        const [classroomRes, summaryRes] = await Promise.all([
          getTeacherClassroom(teacherClassroomId),
          getTeacherClassroomSummary(teacherClassroomId),
        ]);
        const merged = mergeClassroomSummary(classroomRes, summaryRes, teacherClassroomId);
        setClassroom(merged);
      } catch (err) {
        setClassroomError('Failed to load classroom data.');
      } finally {
        setClassroomLoading(false);
      }
    };

    fetchClassroom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherClassroomId]);

  // Transform transcript from API response
  const transformTranscriptData = useCallback(transcriptData => {
    if (!transcriptData || !transcriptData.value) return null;

    const data = transcriptData.value;
    if (data.status === 'success' && data.data && data.data.utterances) {
      return {
        segments: data.data.utterances.map((item, index) => ({
          id: index + 1,
          speaker: item.speaker_tag || `Speaker ${item.speaker_id || index + 1}`,
          text: item.transcript || '',
          startTime: item.start_time || 0,
          endTime: item.end_time || 0,
          confidence: item.confidence || 0,
        })),
      };
    }
    return null;
  }, []);

  // Fetch transcript
  useEffect(() => {
    trackEvent(EVENT_TYPE.CLASSROOM_VIDEO_VIEW, {
      teacher_classroom_id: teacherClassroomId,
    });

    const fetchTranscript = async () => {
      setTranscriptLoading(true);
      try {
        const result = await checkTranscriptStatus({
          objectId: teacherClassroomId,
          videoId: teacherClassroomId,
        });

        if (result.exists && !result.processing) {
          const transformed = transformTranscriptData(result.data);
          setTranscript(transformed);
          setTranscriptLoading(false);
        } else if (result.processing) {
          setTranscriptProcessing(true);
          setTranscriptLoading(false);
          startPolling();
        } else {
          // No transcript found — auto-trigger transcription if we have a video URL
          if (rawVideoUrl) {
            const s3Uri = buildS3Uri(rawVideoUrl);
            if (s3Uri) {
              startTranscribe(
                { file_name: teacherClassroomId, file_url: s3Uri },
                { teacherClassroomId },
              );
              setTranscriptProcessing(true);
              setTranscriptLoading(false);
              startPolling();
            } else {
              setTranscriptLoading(false);
            }
          } else {
            setTranscriptLoading(false);
          }
        }
      } catch (err) {
        setTranscriptError('Failed to load transcript.');
        setTranscriptLoading(false);
      }
    };

    const startPolling = () => {
      let attempts = 0;
      pollingRef.current = setInterval(async () => {
        attempts++;
        try {
          const result = await checkTranscriptStatus({
            objectId: teacherClassroomId,
            videoId: teacherClassroomId,
          });

          if (result.exists && !result.processing) {
            const transformed = transformTranscriptData(result.data);
            setTranscript(transformed);
            setTranscriptProcessing(false);
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        } catch (err) {
          // Continue polling
        }

        if (attempts >= TRANSCRIPT_MAX_ATTEMPTS) {
          setTranscriptProcessing(false);
          setTranscriptError('Transcript generation timed out. Please refresh later.');
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }, TRANSCRIPT_POLL_INTERVAL);
    };

    fetchTranscript();

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherClassroomId, transformTranscriptData, rawVideoUrl]);

  // Video controls
  const handleTogglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (playing) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
  }, [playing]);

  const handleSeek = useCallback(time => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = time;
    setCurrentTime(time);
  }, []);

  const handleSeekBackward = useCallback(() => {
    handleSeek(Math.max(0, currentTime - 5));
  }, [currentTime, handleSeek]);

  const handleSeekForward = useCallback(() => {
    handleSeek(Math.min(duration, currentTime + 5));
  }, [currentTime, duration, handleSeek]);

  const handleBack = () => {
    history.push('/classroom-dashboard', { activeTab: TABS.REGULAR });
  };

  if (classroomLoading) {
    return (
      <Container>
        <Text variant="body1Regular">Loading classroom data...</Text>
      </Container>
    );
  }

  if (classroomError) {
    return (
      <Container>
        <BackButton onClick={handleBack}>← Back to Classroom Analysis Hub</BackButton>
        <ErrorWrapper>
          <Text variant="body1Regular" color="red">
            {classroomError}
          </Text>
        </ErrorWrapper>
      </Container>
    );
  }

  const teacherName = classroom?.teacher_name || '-';
  const studentName = classroom?.student_name;
  const classType = classroom?.class_type || classroom?.program_type || '-';
  const classDate = formatClassDate(classroom?.scheduled_start_datetime);
  const grade = classroom?.student_grade || classroom?.grade || '';

  return (
    <Container>
      <BackButton onClick={handleBack}>← Back to Classroom Analysis Hub</BackButton>

      <HeaderRow>
        <Text variant="h5">{teacherName}</Text>
        {studentName && studentName !== '-' && (
          <Text variant="body2Regular">with {studentName}</Text>
        )}
        <Badge>{classType}</Badge>
        {grade && <Badge>Grade {grade}</Badge>}
        <Badge>{classDate}</Badge>
      </HeaderRow>

      {videoNotComposed ? (
        <ComposingMessage>
          <Text variant="h5">Video is being composed</Text>
          <Separator height={12} transparent />
          <Text variant="body1Regular">
            The recording is being processed into a viewable video. Please wait ~10 minutes.
            This page will automatically load the video once it's ready.
          </Text>
          {composePollingMsg && (
            <>
              <Separator height={12} transparent />
              <Text variant="body2Regular" color={COLORS.GRAY}>
                {composePollingMsg}
              </Text>
            </>
          )}
        </ComposingMessage>
      ) : (
        <ContentGrid>
          <VideoSection>
            {videoError ? (
              <ErrorWrapper>
                <Text variant="body1Regular" color="red">
                  {videoError}
                </Text>
              </ErrorWrapper>
            ) : (
              <VideoPlayer
                videoUrl={videoUrl}
                videoRef={videoRef}
                currentTime={currentTime}
                duration={duration}
                playing={playing}
                playbackRate={playbackRate}
                playerReady={playerReady}
                annotations={[]}
                aiAnalysisMarkers={[]}
                onTimeUpdate={setCurrentTime}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onSeek={handleSeek}
                onPlaybackRateChange={setPlaybackRate}
                onTogglePlay={handleTogglePlay}
                onSeekBackward={handleSeekBackward}
                onSeekForward={handleSeekForward}
                onDurationChange={setDuration}
                onPlayerReady={() => setPlayerReady(true)}
                onVideoError={handleVideoError}
              />
            )}
          </VideoSection>

          <TranscriptSection>
            <TranscriptPanel
              transcript={transcript}
              currentTime={currentTime}
              onSeekToTime={handleSeek}
              isLoading={transcriptLoading}
              isProcessing={transcriptProcessing}
              classroomId={teacherClassroomId}
              demoDate={classDate}
            />
            {transcriptError && (
              <Text
                variant="body2Regular"
                color="red"
                style={{ padding: '12px', textAlign: 'center' }}
              >
                {transcriptError}
              </Text>
            )}
          </TranscriptSection>
        </ContentGrid>
      )}
    </Container>
  );
}

export default ClassroomViewPage;
